import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CreateEscritoVersionRequest } from '@models/escritoVersion/createEscritoVersionRequest.model';
import { EscritoVersionDto } from '@models/escritoVersion/escritoVersionDto.model';
import { EditorStateService } from '@services/editorState/editor-state.service';
import { EscritoVersionService } from '@services/escritoVersion/escritoVersion.service';
import { ConfirmDialogComponent } from '@views/escrito-editor/dialogs/confirm-dialog/confirm-dialog/confirm-dialog.component';
import { VersionDiffDialogComponent } from '@views/escrito-editor/dialogs/version-diff-dialog/version-diff-dialog/version-diff-dialog.component';
import { NgxSpinnerModule } from 'ngx-spinner';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-versiones-panel',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, NgxSpinnerModule, MatFormFieldModule, ReactiveFormsModule, MatProgressSpinnerModule, MatDividerModule,MatChipsModule,MatMenuModule,MatTooltipModule,MatDialogModule,MatSnackBarModule,],
  templateUrl: './versiones-panel.component.html',
  styleUrl: './versiones-panel.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VersionesPanelComponent {

  versiones: EscritoVersionDto[] = [];
  isLoading = false;
  currentEscritoId?: number;
  selectedVersion?: EscritoVersionDto;

  private destroy$ = new Subject<void>();

  constructor(
    private versionService: EscritoVersionService,
    private editorStateService: EditorStateService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.subscribeToActiveTab();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToActiveTab(): void {
    this.editorStateService.getEditorState()
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        const activeTab = state.tabs.find(t => t.id === state.activeTabId);

        if (activeTab?.tipo === 'escrito' && activeTab.entityId) {
          if (this.currentEscritoId !== activeTab.entityId) {
            this.currentEscritoId = activeTab.entityId;
            this.loadVersiones();
          }
        } else {
          this.currentEscritoId = undefined;
          this.versiones = [];
        }
      });
  }

  private loadVersiones(): void {
    if (!this.currentEscritoId) return;

    this.isLoading = true;
    this.versionService.obtenerVersionesPorEscrito(this.currentEscritoId).subscribe({
      next: (versiones) => {
        this.versiones = versiones.sort((a, b) =>
          new Date(b.fechaCreacion).getTime() - new Date(a.fechaCreacion).getTime()
        );
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error cargando versiones:', error);
        this.isLoading = false;
        this.snackBar.open('Error cargando versiones', 'Cerrar', { duration: 3000 });
      }
    });
  }

  onVersionClick(version: EscritoVersionDto): void {
    this.selectedVersion = version;
  }

  onCompareWithCurrent(version: EscritoVersionDto): void {
    const activeTab = this.editorStateService.getActiveTab();
    if (!activeTab) return;

    const dialogRef = this.dialog.open(VersionDiffDialogComponent, {
      width: '90vw',
      height: '80vh',
      maxWidth: '1200px',
      data: {
        originalContent: activeTab.contenido,
        versionContent: version.cuerpoHTML || '',
        originalTitle: `${activeTab.titulo} (Actual)`,
        versionTitle: `Versión ${version.numeroVersion} - ${this.formatDate(new Date(version.fechaCreacion))}`
      }
    });
  }

  onRestoreVersion(version: EscritoVersionDto): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Restaurar Versión',
        message: `¿Estás seguro de que deseas restaurar la versión ${version.numeroVersion}? Se perderán los cambios actuales.`,
        confirmText: 'Restaurar',
        cancelText: 'Cancelar'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.versionService.restaurarVersion(version.id).subscribe({
          next: () => {
            this.snackBar.open('Versión restaurada exitosamente', 'Cerrar', { duration: 3000 });
            this.loadVersiones();

            // Actualizar el contenido en el editor
            const activeTab = this.editorStateService.getActiveTab();
            if (activeTab) {
              this.editorStateService.updateTabContent(activeTab.id, version.cuerpoHTML || '');
            }
          },
          error: (error) => {
            console.error('Error restaurando versión:', error);
            this.snackBar.open('Error al restaurar la versión', 'Cerrar', { duration: 3000 });
          }
        });
      }
    });
  }

  onDeleteVersion(version: EscritoVersionDto): void {
    if (this.versiones.length <= 1) {
      this.snackBar.open('No se puede eliminar la única versión', 'Cerrar', { duration: 3000 });
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Eliminar Versión',
        message: `¿Estás seguro de que deseas eliminar la versión ${version.numeroVersion}?`,
        confirmText: 'Eliminar',
        cancelText: 'Cancelar'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.versionService.delete(version.id).subscribe({
          next: () => {
            this.snackBar.open('Versión eliminada exitosamente', 'Cerrar', { duration: 3000 });
            this.loadVersiones();
            if (this.selectedVersion?.id === version.id) {
              this.selectedVersion = undefined;
            }
          },
          error: (error) => {
            console.error('Error eliminando versión:', error);
            this.snackBar.open('Error al eliminar la versión', 'Cerrar', { duration: 3000 });
          }
        });
      }
    });
  }

  onCreateVersion(): void {
    const activeTab = this.editorStateService.getActiveTab();
    if (!activeTab || !activeTab.entityId) return;

    const newVersion = new CreateEscritoVersionRequest
    newVersion.descripcion = `Versión creada manualmente`
    newVersion.cuerpoHTML = activeTab.contenido
    newVersion.idEscrito = activeTab.entityId

    this.versionService.create(newVersion).subscribe({
      next: () => {
        this.snackBar.open('Nueva versión creada exitosamente', 'Cerrar', { duration: 3000 });
        this.loadVersiones();
      },
      error: (error) => {
        console.error('Error creando versión:', error);
        this.snackBar.open('Error al crear la versión', 'Cerrar', { duration: 3000 });
      }
    });
  }

  onCleanupOldVersions(): void {
    if (!this.currentEscritoId) return;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Limpiar Versiones Antiguas',
        message: 'Se mantendrán las 5 versiones más recientes. ¿Continuar?',
        confirmText: 'Limpiar',
        cancelText: 'Cancelar'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && this.currentEscritoId) {
        this.versionService.eliminarNVersionesAntiguas(this.currentEscritoId, 5).subscribe({
          next: () => {
            this.snackBar.open('Versiones antiguas eliminadas', 'Cerrar', { duration: 3000 });
            this.loadVersiones();
          },
          error: (error) => {
            console.error('Error limpiando versiones:', error);
            this.snackBar.open('Error al limpiar versiones', 'Cerrar', { duration: 3000 });
          }
        });
      }
    });
  }

  formatDate(date: Date): string {
    return new Intl.DateTimeFormat('es-AR', {
      dateStyle: 'short',
      timeStyle: 'short'
    }).format(new Date(date));
  }

  getVersionDescription(version: EscritoVersionDto): string {
    return version.descripcion || `Versión ${version.numeroVersion}`;
  }

  isLatestVersion(version: EscritoVersionDto): boolean {
    return this.versiones.length > 0 && this.versiones[0].id === version.id;
  }
}

